import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createBlog, getBlogById, updateBlog } from '../api/blogApi'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import QuillResize from 'quill-resize-image'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { addCategory, getCategories } from '../api/categoryApi'

ReactQuill.Quill.register('modules/resize', QuillResize)

const AddBlogs = ({ blogId = null, isEdit = false }) => {
  const navigate = useNavigate()
  const [editorContent, setEditorContent] = useState('')
  const [imagePreview, setImagePreview] = useState(null)
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [category, setCategory] = useState('')

  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      brandName: '',
      title: '',
      articleDate: '',
      description: '',
      writtenBy: '',
      pcCredit: '',
      categoryId: '',
      image: null,
      showInSlider: false,
    },
  })

  const brandName = watch('brandName')
  const title = watch('title')
  const articleDate = watch('articleDate')
  const writtenBy = watch('writtenBy')
  const imageFile = watch('image')

  // Check if all mandatory fields are filled
  const isFormValid = useMemo(() => {
    const hasBrandName = brandName?.trim().length > 0
    const hasTitle = title?.trim().length > 0
    const hasArticleDate = articleDate?.trim().length > 0
    const hasWrittenBy = writtenBy?.trim().length > 0

    const hasDescription =
      editorContent?.replace(/<[^>]*>/g, '').trim().length > 0
    const hasImage = isEdit ? true : imageFile && imageFile[0]

    return (
      hasBrandName &&
      hasTitle &&
      hasArticleDate &&
      hasWrittenBy &&
      hasDescription &&
      hasImage
    )
  }, [
    brandName,
    title,
    articleDate,
    writtenBy,
    editorContent,
    imageFile,
    isEdit,
  ])

  // Fetch category list for dropdown
  const { data: categories, isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  })

  const { mutate: handleAddNewCategory, isPending } = useMutation({
    mutationFn: addCategory,
    onSuccess: () => {
      toast.success('Category added successfully')
      queryClient.invalidateQueries(['categories'])
      setShowNewCategory(false)
      setCategory('')
    },
    onError: (error) => {
      toast.error('Failed to add category')
      console.error('Error adding category:', error)
    },
  })

  // Fetch deal data for editing
  const { data: blogData, isLoading } = useQuery({
    queryKey: ['blogs', blogId],
    queryFn: () => getBlogById(blogId),
    enabled: isEdit && !!blogId,
    onError: (error) => {
      toast.error('Failed to load data')
      console.error('Error loading Opinions:', error)
      navigate(`/dealdashboard/blog`)
    },
  })

  // Populate form
  const populateForm = useCallback(
    (data) => {
      if (!data) {
        toast.error('No data available to populate form')
        return
      }

      setValue('title', data.title || '')
      setValue('brandName', data.brandName || '')
      setValue('articleDate', data.articleDate || '')
      setValue('description', data.description || '')
      setValue('writtenBy', data.writtenBy || '')
      setValue('pcCredit', data.pcCredit || '')
      setEditorContent(data.description || '')
      setValue('categoryId', data.categoryId || '')
      setValue('showInSlider', !!data.showInSlider)

      if (data.imageUrl) {
        setImagePreview(data.imageUrl)
      }
    },
    [setValue]
  )

  useEffect(() => {
    if (isEdit && blogData) {
      populateForm(blogData)
    }
  }, [blogData, isEdit, populateForm])

  // Handle image preview
  useEffect(() => {
    if (imageFile && imageFile[0]) {
      const previewUrl = URL.createObjectURL(imageFile[0])
      setImagePreview(previewUrl)
      return () => URL.revokeObjectURL(previewUrl)
    }
  }, [imageFile])

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => createBlog(data),
    onSuccess: () => {
      toast.success(`Opinion added successfully!`)
      reset()
      setImagePreview(null)
      navigate(`/dealdashboard/blog`)
    },
    onError: (error) => {
      console.error('Error creating opinion:', error)
      toast.error(
        error.response?.data?.message ||
          'Error creating opinion. Please try again.'
      )
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data) => updateBlog(blogId, data),
    onSuccess: () => {
      toast.success(`Opinion updated successfully!`)
      navigate(`/dealdashboard/blog`)
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(error.response?.data?.message || 'Failed to update Opinion')
    },
  })

  const handleImageChange = useCallback(
    (e, fieldName) => {
      const files = e.target.files
      if (files && files.length > 0) {
        setValue(fieldName, files)
      }
    },
    [setValue]
  )

  const onSubmit = async (data) => {
    if (!isEdit && (!data.image || !data.image[0])) {
      toast.error('Please upload at least one image')
      return
    }

    if (isEdit && blogId) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  if (isEdit && isLoading) {
    return (
      <div className="max-w-8xl text-center mx-auto px-4 py-6">
        <p className="text-gray-600">{`Loading Opinion data...`}</p>
      </div>
    )
  }

  const toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video', 'formula'],
    [{ header: 1 }, { header: 2 }],
    [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ direction: 'rtl' }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    [{ align: [] }],
    ['clean'],
    ['resize'],
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="max-w-8xl text-left mx-auto px-4 py-6">
        <div className="grid md:grid-cols-2 gap-6 mb-4">
          {/* Brand Name */}
          <div className="bg-white p-6 rounded-xl shadow">
            <h2 className="font-semibold text-gray-800 mb-2">
              Brand Name <span className="text-red-500">*</span>
            </h2>
            <input
              type="text"
              placeholder="Enter brand name"
              {...register('brandName', {
                required: 'Brand name is required',
              })}
              className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                errors.brandName
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
              }`}
            />
            {errors.brandName && (
              <p className="text-red-500 text-sm mt-1">
                {errors.brandName.message}
              </p>
            )}
          </div>

          {/* Written By */}
          <div className="bg-white p-6 rounded-xl shadow">
            <h2 className="font-semibold text-gray-800 mb-2">
              Written By <span className="text-red-500">*</span>
            </h2>
            <input
              type="text"
              placeholder="Enter name"
              {...register('writtenBy', {
                required: 'Writer Name is required',
              })}
              className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                errors.writtenBy
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
              }`}
            />
            {errors.writtenBy && (
              <p className="text-red-500 text-sm mt-1">
                {errors.writtenBy.message}
              </p>
            )}
          </div>

          {/* Brand Logo */}
          <div className="bg-white p-6 rounded-xl shadow">
            <h2 className="font-semibold text-gray-800 mb-2">
              Brand Logo {!isEdit && <span className="text-red-500">*</span>}
            </h2>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Brand Logo Preview"
                className="mt-4 max-h-64 object-cover rounded-lg border border-gray-200"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e, 'image')}
              {...register('image', {
                required: isEdit ? false : 'Image is required',
              })}
              className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100"
            />
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">
                {errors.image.message}
              </p>
            )}
          </div>

          {/* category */}
          <div className="bg-white p-6 rounded-xl shadow">
            <h2 className="font-semibold text-gray-800 mb-2">
              Category <span className="text-red-500">*</span>
            </h2>

            {isCategoriesLoading ? (
              <div className="flex items-center mt-2 text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              </div>
            ) : (
              <>
                <select
                  {...register('categoryId', {
                    required: 'Category is required',
                  })}
                  onChange={(e) => {
                    if (e.target.value === 'new') {
                      setShowNewCategory(true)
                    } else {
                      setShowNewCategory(false)
                    }
                  }}
                  className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                    errors.categoryId
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
                  }`}
                >
                  <option value="">Select Category</option>

                  {categories?.map((category) => (
                    <option key={category?.id} value={category?.id}>
                      {category?.name}
                    </option>
                  ))}

                  <option value="new">Add New Category</option>
                </select>

                {showNewCategory && (
                  <div className="flex items-center justify-between mt-2 space-x-2 border rounded-lg border-gray-300">
                    <input
                      type="text"
                      value={category}
                      placeholder="Enter new category name"
                      onChange={(e) => setCategory(e.target.value)}
                      className="p-3 w-full text-base outline-none"
                    />

                    {isPending ? (
                      <div className="flex items-center text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAddNewCategory(category)}
                        className="bg-orange-500 text-white p-3 rounded-lg hover:bg-orange-600"
                      >
                        Add
                      </button>
                    )}
                  </div>
                )}

                {errors.categoryId && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.categoryId.message}
                  </p>
                )}
              </>
            )}
          </div>

          {/* picture credit */}
          <div className=" bg-white/35 p-3 rounded-xl shadow">
            <div>
              <h2 className="font-semibold text-gray-800 mb-2">
                Picture Credit
              </h2>
              <input
                type="text"
                placeholder="eg : Photo by Ishant Chauhan/Hindustan Times"
                {...register('pcCredit')}
                className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                  errors.pcCredit
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
                }`}
              />
              {errors.pcCredit && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.pcCredit.message}
                </p>
              )}
            </div>
          </div>

          {/* Heading */}
          <div className="bg-white p-6 rounded-xl shadow col-span-2">
            <h2 className="font-semibold text-gray-800 mb-2">
              Heading <span className="text-red-500">*</span>
            </h2>
            <input
              type="text"
              placeholder="Enter headline..."
              {...register('title', {
                required: 'Heading is required',
              })}
              className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                errors.title
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
              }`}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="bg-white p-6 rounded-xl shadow col-span-2">
            <h2 className="font-semibold text-gray-800 mb-2">
              Content <span className="text-red-500">*</span>
            </h2>
            <ReactQuill
              theme="snow"
              value={editorContent}
              onChange={(content) => {
                setEditorContent(content)
                setValue('description', content)
              }}
              modules={{
                toolbar: toolbarOptions,
                resize: { displaySize: true },
              }}
              placeholder="Write the content here..."
              className={`${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Article Date */}
          <div className="bg-white p-6 rounded-xl shadow">
            <h2 className="font-semibold text-gray-800 mb-2">
              Date of Publishing <span className="text-red-500">*</span>
            </h2>
            <input
              type="date"
              {...register('articleDate', {
                required: 'Date is required',
              })}
              className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                errors.articleDate
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
              }`}
            />
            {errors.articleDate && (
              <p className="text-red-500 text-sm mt-1">
                {errors.articleDate.message}
              </p>
            )}
          </div>

          {/* Show in Slider */}
          <div className="bg-white p-6 rounded-xl shadow flex items-center gap-3">
            <input
              type="checkbox"
              id="showInSlider"
              {...register('showInSlider')}
              className="w-5 h-5 accent-orange-500 cursor-pointer"
            />
            <label
              htmlFor="showInSlider"
              className="font-semibold text-gray-800 cursor-pointer"
            >
              Show in Slider
              <span className="block font-normal text-sm text-gray-500">
                Adds this article to the homepage slider automatically — no
                separate slider entry needed.
              </span>
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8 text-center">
          <button
            type="submit"
            disabled={
              !isFormValid ||
              isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending
            }
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 text-white text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all"
          >
            {isSubmitting ||
            createMutation.isPending ||
            updateMutation.isPending
              ? 'Processing...'
              : isEdit
                ? `Update Opinion`
                : `Add Opinion`}
          </button>

          {/* Form validation status message */}
          {!isFormValid && (
            <p className="text-gray-500 text-sm mt-3">
              Please fill all mandatory fields to enable the submit button
            </p>
          )}
        </div>
      </div>
    </form>
  )
}

export default AddBlogs
